import { Button } from "@/components/ui/button";

const comparisonData = {
  categories: [
    {
      name: "Assessment Flexibility",
      features: [
        {
          capability: "Video Assessments",
          videoOnly: "✓",
          codeAssessment: "✗",
          genericTests: "Limited",
          testera: "✓"
        },
        {
          capability: "Code Assessments (25+ Languages)",
          videoOnly: "✗",
          codeAssessment: "✓",
          genericTests: "✗",
          testera: "✓"
        },
        {
          capability: "Essay/Written Response",
          videoOnly: "✗",
          codeAssessment: "✗",
          genericTests: "✓",
          testera: "✓"
        },
        {
          capability: "Audio Recording",
          videoOnly: "✗",
          codeAssessment: "✗",
          genericTests: "✗",
          testera: "✓"
        },
        {
          capability: "Custom Questions Across All Formats",
          videoOnly: "Limited",
          codeAssessment: "Limited",
          genericTests: "Pre-built only",
          testera: "✓ Unlimited"
        },
        {
          capability: "All Formats in One Assessment",
          videoOnly: "✗",
          codeAssessment: "✗",
          genericTests: "✗",
          testera: "✓"
        }
      ]
    },
    {
      name: "AI-Powered Generation & Customization",
      features: [
        {
          capability: "Generate Questions from Job Description",
          videoOnly: "✗",
          codeAssessment: "✗",
          genericTests: "✗",
          testera: "✓ Upload JD, get questions"
        },
        {
          capability: "AI Skill Extraction",
          videoOnly: "✗",
          codeAssessment: "✗",
          genericTests: "✗",
          testera: "✓ Auto-detects required skills"
        },
        {
          capability: "Edit AI-Generated Questions",
          videoOnly: "N/A",
          codeAssessment: "N/A",
          genericTests: "N/A",
          testera: "✓ Full editorial control"
        },
        {
          capability: "Company-Specific Customization",
          videoOnly: "Manual setup",
          codeAssessment: "Manual setup",
          genericTests: "✗",
          testera: "✓ Customized from your JD"
        }
      ]
    },
    {
      name: "Implementation & Deployment",
      features: [
        {
          capability: "Setup Time",
          videoOnly: "3-6 months",
          codeAssessment: "2-4 weeks",
          genericTests: "1-2 weeks",
          testera: "Same day"
        },
        {
          capability: "Technical Expertise Required",
          videoOnly: "Medium (IT involvement)",
          codeAssessment: "High (Dev setup)",
          genericTests: "Low",
          testera: "None (HR self-serve)"
        },
        {
          capability: "IT Department Required",
          videoOnly: "Yes",
          codeAssessment: "Yes",
          genericTests: "No",
          testera: "No"
        }
      ]
    }
  ]
};

const renderCell = (value: string) => {
  if (value === "✓") {
    return <span className="text-green-600 text-lg">✓</span>;
  }
  if (value === "✗") {
    return <span className="text-muted-foreground text-lg">✗</span>;
  }
  if (value === "Limited") {
    return <span className="text-sm text-orange-600">Limited</span>;
  }
  if (value.includes("✓")) {
    return <span className="text-sm font-medium text-foreground">{value}</span>;
  }
  return <span className="text-sm text-muted-foreground">{value}</span>;
};

const renderTesteraCell = (value: string) => {
  const textOnly = value.replace("✓ ", "").trim();
  
  if (value === "✓") {
    return <span className="text-green-600 text-lg font-semibold">✓</span>;
  }
  
  return <span className="text-green-600 text-sm font-semibold">{textOnly}</span>;
};

export const CompetitiveComparisonTable = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-6 lg:px-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Stop Stitching Together{" "}
            <span className="text-secondary">Multiple Assessment Platforms</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            One AI-powered platform for every assessment format. Deploy in hours, not months.
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl shadow-2xl">
          <table className="w-full border-collapse bg-card">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left py-5 px-6 text-foreground text-sm font-semibold w-[30%] hidden md:table-cell border-r border-border">
                  Capability
                </th>
                <th className="text-center py-5 px-4 text-foreground text-sm font-semibold border-r border-border">
                  Video-Only
                </th>
                <th className="text-center py-5 px-4 text-foreground text-sm font-semibold border-r border-border">
                  Code Assessment
                </th>
                <th className="text-center py-5 px-4 text-foreground text-sm font-semibold border-r border-border">
                  Generic Tests
                </th>
                <th className="text-center py-5 px-4 border-r border-border">
                  <div className="flex items-center justify-center space-x-2">
                    <img 
                      src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" 
                      alt="Testera Logo" 
                      className="h-6 w-6"
                    />
                    <span className="text-sm font-bold text-foreground tracking-tight">
                      Testera
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            
            <tbody>
              {comparisonData.categories.map((category, catIndex) => (
                <>
                  <tr key={`cat-${catIndex}`} className="bg-muted/30">
                    <td colSpan={5} className="py-3 px-6 text-xs font-bold text-foreground uppercase tracking-wide">
                      {category.name}
                    </td>
                  </tr>
                  {category.features.map((feature, featureIndex) => (
                    <tr 
                      key={`feature-${catIndex}-${featureIndex}`}
                      className="border-b border-border hover:bg-muted/10 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-foreground hidden md:table-cell">
                        {feature.capability}
                      </td>
                      <td className="text-center py-4 px-2">
                        {renderCell(feature.videoOnly)}
                      </td>
                      <td className="text-center py-4 px-2">
                        {renderCell(feature.codeAssessment)}
                      </td>
                      <td className="text-center py-4 px-2">
                        {renderCell(feature.genericTests)}
                      </td>
                      <td className="text-center py-4 px-2 bg-green-50/50">
                        {renderTesteraCell(feature.testera)}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>

          <div className="bg-muted/30">
            <div className="flex items-center justify-between py-8 px-8">
              <h3 className="text-xl font-bold text-foreground">
                See Your First Assessment Created in 10 Minutes
              </h3>
              <div className="flex justify-end" style={{ width: '20%' }}>
                <Button 
                  size="lg" 
                  className="bg-secondary hover:bg-secondary-hover text-secondary-foreground shadow-lg hover:shadow-xl transition-all"
                >
                  Sign Up Now →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
